import { ACTION_TYPES, IMPORTANCE_TIERS, NON_ACTOR_SPEAKER_KINDS, ROLE_TYPES, } from "./types.js";
import { isV4Id } from "./ids.js";
import { validateActionTimestampOrder } from "./timestamp.js";
import { isSceneTime } from "./types.js";
const isObj = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const isStr = (value) => typeof value === "string";
const isArr = (value) => Array.isArray(value);
function isJsonValue(value) {
    if (value === null
        || typeof value === "string"
        || typeof value === "number"
        || typeof value === "boolean")
        return true;
    if (Array.isArray(value))
        return value.every(isJsonValue);
    return isObj(value) && Object.values(value).every(isJsonValue);
}
function validateExtend(value, path, err) {
    if (value !== undefined && (!isObj(value) || !Object.values(value).every(isJsonValue))) {
        err("extend", path, "extend must be an object containing JSON values");
    }
}
function isSorted(values) {
    for (let index = 1; index < values.length; index++) {
        if (values[index - 1] > values[index])
            return false;
    }
    return true;
}
export function validateScriptV4(doc) {
    const errors = [];
    const warnings = [];
    const err = (code, path, message, repairHint) => errors.push({ code, path, message, ...(repairHint ? { repair_hint: repairHint } : {}) });
    const warn = (code, path, message, repairHint) => warnings.push({ code, path, message, ...(repairHint ? { repair_hint: repairHint } : {}) });
    if (!isObj(doc)) {
        return {
            ok: false,
            errors: [{ code: "not_object", path: "$", message: "script must be an object" }],
            warnings,
        };
    }
    if (doc.version !== 4)
        err("version", "$.version", "version must be 4");
    for (const key of ["title", "worldview", "style"]) {
        if (!isStr(doc[key]))
            err("field", `$.${key}`, `${key} must be a string`);
    }
    for (const key of ["worldview_raw", "synopsis", "logline", "theme"]) {
        if (doc[key] !== undefined && !isStr(doc[key]))
            err("field", `$.${key}`, `${key} must be a string`);
    }
    if (doc.main_characters !== undefined
        && (!isArr(doc.main_characters)
            || !doc.main_characters.every((item) => isObj(item) && isStr(item.name) && isStr(item.role)))) {
        err("main_characters", "$.main_characters", "main_characters must contain {name, role} string records");
    }
    validateExtend(doc.extend, "$.extend", err);
    const assetKinds = new Map();
    const stateIds = new Map();
    const actorNames = new Set();
    const collectAssets = (list, kind, idField, nameField, path) => {
        if (!isArr(list)) {
            err("field", path, `${path} must be an array`);
            return;
        }
        const ids = new Set();
        list.forEach((raw, index) => {
            const itemPath = `${path}[${index}]`;
            if (!isObj(raw)) {
                err("asset", itemPath, "asset must be an object");
                return;
            }
            const id = raw[idField];
            if (!isV4Id(kind, id)) {
                err("asset_id", `${itemPath}.${idField}`, `${idField} must be ${kind} ID`);
                return;
            }
            if (ids.has(id))
                err("dup_id", `${itemPath}.${idField}`, `duplicate ${idField} ${id}`);
            ids.add(id);
            assetKinds.set(id, kind);
            if (!isStr(raw[nameField]) || raw[nameField].trim() === "") {
                err("asset_name", `${itemPath}.${nameField}`, `${nameField} must be a non-empty string`);
            }
            else if (kind === "actor") {
                const nameKey = raw[nameField].trim().toLocaleLowerCase();
                if (actorNames.has(nameKey)) {
                    err("dup_actor_name", `${itemPath}.${nameField}`, `duplicate actor name ${raw[nameField]}`);
                }
                actorNames.add(nameKey);
            }
            if (!isStr(raw.importance) || !IMPORTANCE_TIERS.includes(raw.importance)) {
                err("importance", `${itemPath}.importance`, `importance must be ${IMPORTANCE_TIERS.join("|")}`);
            }
            if (kind === "actor"
                && raw.role_type !== undefined
                && (!isStr(raw.role_type) || !ROLE_TYPES.includes(raw.role_type))) {
                err("role_type", `${itemPath}.role_type`, `role_type must be ${ROLE_TYPES.join("|")}`);
            }
            if (!isStr(raw.description))
                err("asset_desc", `${itemPath}.description`, "description must be a string");
            if (raw.aliases !== undefined
                && (!isArr(raw.aliases) || !raw.aliases.every(isStr))) {
                err("aliases", `${itemPath}.aliases`, "aliases must be an array of strings");
            }
            else if (isArr(raw.aliases) && raw.aliases.every(isStr)) {
                const seenAliases = new Set();
                const assetName = isStr(raw[nameField]) ? raw[nameField] : "";
                raw.aliases.forEach((alias, aliasIndex) => {
                    if (alias === "") {
                        err("alias_empty", `${itemPath}.aliases[${aliasIndex}]`, "alias must not be empty");
                    }
                    if (alias === assetName) {
                        err("alias_equals_name", `${itemPath}.aliases[${aliasIndex}]`, "alias must differ from the asset name");
                    }
                    if (seenAliases.has(alias)) {
                        err("dup_alias", `${itemPath}.aliases[${aliasIndex}]`, `duplicate alias ${alias}`);
                    }
                    seenAliases.add(alias);
                });
            }
            validateExtend(raw.extend, `${itemPath}.extend`, err);
            const ownerStates = new Set();
            const ownerStateNames = new Set();
            let defaults = 0;
            if (!isArr(raw.states) || raw.states.length === 0) {
                err("states", `${itemPath}.states`, "states must be a non-empty array");
            }
            else {
                raw.states.forEach((state, stateIndex) => {
                    const statePath = `${itemPath}.states[${stateIndex}]`;
                    if (!isObj(state)) {
                        err("state", statePath, "state must be an object");
                        return;
                    }
                    const stateId = state.state_id;
                    if (stateId !== "default" && !isV4Id("state", stateId)) {
                        err("state_id", `${statePath}.state_id`, "state_id must be default or st plus six Base58 characters");
                        return;
                    }
                    if (ownerStates.has(stateId))
                        err("dup_state", `${statePath}.state_id`, `duplicate state_id ${stateId}`);
                    ownerStates.add(stateId);
                    if (stateId === "default")
                        defaults++;
                    if (!isStr(state.state_name) || state.state_name.trim() === "") {
                        err("state_name", `${statePath}.state_name`, "state_name must be a non-empty string");
                    }
                    else {
                        if (ownerStateNames.has(state.state_name)) {
                            err("dup_state_name", `${statePath}.state_name`, `duplicate state_name ${state.state_name}`);
                        }
                        ownerStateNames.add(state.state_name);
                    }
                    if (!isStr(state.description))
                        err("state_description", `${statePath}.description`, "description must be a string");
                    validateExtend(state.extend, `${statePath}.extend`, err);
                });
            }
            if (defaults !== 1) {
                err("default_state", `${itemPath}.states`, `exactly one default state required, found ${defaults}`);
            }
            stateIds.set(id, ownerStates);
        });
    };
    collectAssets(doc.actors, "actor", "actor_id", "actor_name", "$.actors");
    collectAssets(doc.locations, "location", "location_id", "location_name", "$.locations");
    collectAssets(doc.props, "prop", "prop_id", "prop_name", "$.props");
    const validateStateSet = (assetId, rawStateIds, path) => {
        if (!isArr(rawStateIds) || rawStateIds.length === 0 || !rawStateIds.every(isStr)) {
            err("state_ids", path, "state_ids must be a non-empty array of strings");
            return [];
        }
        const ids = rawStateIds;
        const seen = new Set();
        ids.forEach((stateId, index) => {
            if (seen.has(stateId))
                err("dup_state_ref", `${path}[${index}]`, `duplicate state_id ${stateId}`);
            seen.add(stateId);
            if (!stateIds.get(assetId)?.has(stateId)) {
                err("state_ref", `${path}[${index}]`, `state ${stateId} not found on ${assetId}`);
            }
        });
        if (!isSorted(ids))
            err("state_ids_order", path, "state_ids must be sorted lexically");
        return ids;
    };
    const episodeIds = new Set();
    const sceneIds = new Set();
    const actionIds = new Set();
    if (!isArr(doc.episodes)) {
        err("episodes", "$.episodes", "episodes must be an array");
    }
    else {
        doc.episodes.forEach((episode, episodeIndex) => {
            const episodePath = `$.episodes[${episodeIndex}]`;
            if (!isObj(episode)) {
                err("episode", episodePath, "episode must be an object");
                return;
            }
            if (!isV4Id("episode", episode.episode_id)) {
                err("episode_id", `${episodePath}.episode_id`, "episode_id must be ep plus six Base58 characters");
            }
            else {
                if (episodeIds.has(episode.episode_id)) {
                    err("dup_episode_id", `${episodePath}.episode_id`, `duplicate episode_id ${episode.episode_id}`);
                }
                episodeIds.add(episode.episode_id);
            }
            for (const key of ["title", "synopsis"]) {
                if (episode[key] !== undefined && !isStr(episode[key])) {
                    err("field", `${episodePath}.${key}`, `${key} must be a string`);
                }
            }
            validateExtend(episode.extend, `${episodePath}.extend`, err);
            if (!isArr(episode.scenes)) {
                err("scenes", `${episodePath}.scenes`, "scenes must be an array");
                return;
            }
            episode.scenes.forEach((scene, sceneIndex) => {
                const scenePath = `${episodePath}.scenes[${sceneIndex}]`;
                if (!isObj(scene)) {
                    err("scene", scenePath, "scene must be an object");
                    return;
                }
                if (!isV4Id("scene", scene.scene_id)) {
                    err("scene_id", `${scenePath}.scene_id`, "scene_id must be scn plus six Base58 characters");
                }
                else {
                    if (sceneIds.has(scene.scene_id)) {
                        err("dup_scene_id", `${scenePath}.scene_id`, `duplicate scene_id ${scene.scene_id}`);
                    }
                    sceneIds.add(scene.scene_id);
                }
                validateExtend(scene.extend, `${scenePath}.extend`, err);
                if (scene.environment === undefined
                    || (isObj(scene.environment)
                        && (!isStr(scene.environment.time) || scene.environment.time.trim() === ""))) {
                    warn("scene_time_missing", `${scenePath}.environment.time`, "scene time is required for production review");
                }
                else if (!isObj(scene.environment)
                    || (scene.environment.space !== undefined && !isStr(scene.environment.space))
                    || !isStr(scene.environment.time)) {
                    err("environment", `${scenePath}.environment`, "environment must contain optional string space and a time string");
                }
                else if (!isSceneTime(scene.environment.time)) {
                    err("scene_time", `${scenePath}.environment.time`, "scene time must be day|night|dawn|dusk");
                }
                if (scene.location !== null) {
                    if (!isObj(scene.location) || !isV4Id("location", scene.location.location_id)) {
                        err("location", `${scenePath}.location`, "location must be null or a typed v4 location reference");
                    }
                    else {
                        if ("state_id" in scene.location) {
                            err("v3_field", `${scenePath}.location.state_id`, "Script v4 scene references use state_ids");
                        }
                        if (assetKinds.get(scene.location.location_id) !== "location") {
                            err("location_ref", `${scenePath}.location.location_id`, `location ${scene.location.location_id} not found`);
                        }
                        validateStateSet(scene.location.location_id, scene.location.state_ids, `${scenePath}.location.state_ids`);
                    }
                }
                const sceneActorStates = new Map();
                const validateRefs = (refs, kind, idField, path) => {
                    if (!isArr(refs)) {
                        err("field", path, `${path} must be an array`);
                        return;
                    }
                    const seen = new Set();
                    refs.forEach((ref, refIndex) => {
                        const refPath = `${path}[${refIndex}]`;
                        if (!isObj(ref) || !isV4Id(kind, ref[idField])) {
                            err("ref", refPath, `${idField} must be a typed v4 ID`);
                            return;
                        }
                        const assetId = ref[idField];
                        if ("state_id" in ref) {
                            err("v3_field", `${refPath}.state_id`, "Script v4 scene references use state_ids");
                        }
                        if (seen.has(assetId))
                            err("dup_asset_ref", `${refPath}.${idField}`, `duplicate ${kind} ref ${assetId}`);
                        seen.add(assetId);
                        if (assetKinds.get(assetId) !== kind) {
                            err("asset_ref", `${refPath}.${idField}`, `${kind} ${assetId} not found`);
                        }
                        const ids = validateStateSet(assetId, ref.state_ids, `${refPath}.state_ids`);
                        if (kind === "actor")
                            sceneActorStates.set(assetId, new Set(ids));
                    });
                };
                validateRefs(scene.actors, "actor", "actor_id", `${scenePath}.actors`);
                validateRefs(scene.props, "prop", "prop_id", `${scenePath}.props`);
                if (!isArr(scene.actions)) {
                    err("actions", `${scenePath}.actions`, "actions must be an array");
                    return;
                }
                scene.actions.forEach((action, actionIndex) => {
                    const actionPath = `${scenePath}.actions[${actionIndex}]`;
                    if (!isObj(action)) {
                        err("action", actionPath, "action must be an object");
                        return;
                    }
                    if (!isV4Id("action", action.action_id)) {
                        err("action_id", `${actionPath}.action_id`, "action_id must be ac plus six Base58 characters");
                    }
                    else {
                        if (actionIds.has(action.action_id)) {
                            err("dup_action_id", `${actionPath}.action_id`, `duplicate action_id ${action.action_id}`);
                        }
                        actionIds.add(action.action_id);
                    }
                    if (!isStr(action.type) || !ACTION_TYPES.includes(action.type)) {
                        err("action_type", `${actionPath}.type`, `invalid action type ${String(action.type)}`);
                    }
                    if (!isStr(action.content))
                        err("content", `${actionPath}.content`, "content must be a string");
                    if ("emotion" in action)
                        err("v3_field", `${actionPath}.emotion`, "Script v4 does not allow emotion");
                    validateExtend(action.extend, `${actionPath}.extend`, err);
                    const speaking = action.type === "dialogue" || action.type === "inner_thought";
                    if (speaking) {
                        if (action.parenthetical !== undefined && !isStr(action.parenthetical)) {
                            err("parenthetical", `${actionPath}.parenthetical`, "parenthetical must be a string");
                        }
                        if (!isObj(action.speaker)) {
                            err("speaker", `${actionPath}.speaker`, "dialogue/inner_thought requires a speaker");
                        }
                        else if (isStr(action.speaker.actor_id)) {
                            if ("kind" in action.speaker || "label" in action.speaker) {
                                err("speaker_shape", `${actionPath}.speaker`, "actor speaker cannot contain non-actor speaker fields");
                            }
                            const actorId = action.speaker.actor_id;
                            if (!isV4Id("actor", actorId) || assetKinds.get(actorId) !== "actor") {
                                err("speaker_actor", `${actionPath}.speaker.actor_id`, `actor ${actorId} not found`);
                            }
                            const sceneStates = sceneActorStates.get(actorId);
                            const ownedStates = stateIds.get(actorId);
                            const speakerState = isStr(action.speaker.state_id)
                                && ownedStates?.has(action.speaker.state_id)
                                ? action.speaker.state_id
                                : undefined;
                            const defaultState = ownedStates?.has("default")
                                ? "default"
                                : ownedStates?.values().next().value;
                            const repairStates = speakerState ? [speakerState] : defaultState ? [defaultState] : [];
                            const sceneRefRepair = isV4Id("scene", scene.scene_id)
                                && isV4Id("actor", actorId)
                                && repairStates.length > 0
                                ? `scriptctl scene-ref ${scene.scene_id} ${actorId} ${repairStates.map((id) => `--state ${id}`).join(" ")} -p "$SCRIPT"`
                                : undefined;
                            if (!sceneStates) {
                                err("actor_not_in_scene", `${actionPath}.speaker.actor_id`, `actor ${actorId} is not referenced by this scene`, sceneRefRepair);
                            }
                            if (action.speaker.state_id !== undefined
                                && (!isStr(action.speaker.state_id) || !sceneStates?.has(action.speaker.state_id))) {
                                const nextStates = speakerState
                                    ? [...new Set([...(sceneStates ?? []), speakerState])].sort()
                                    : [];
                                const stateRepair = isV4Id("scene", scene.scene_id)
                                    && isV4Id("actor", actorId)
                                    && nextStates.length > 0
                                    ? `scriptctl scene-ref ${scene.scene_id} ${actorId} ${nextStates.map((id) => `--state ${id}`).join(" ")} -p "$SCRIPT"`
                                    : sceneRefRepair;
                                err("speaker_state", `${actionPath}.speaker.state_id`, `speaker state ${String(action.speaker.state_id)} is not in this scene's actor state_ids`, stateRepair);
                            }
                        }
                        else if (isStr(action.speaker.kind)) {
                            if ("actor_id" in action.speaker || "state_id" in action.speaker) {
                                err("speaker_shape", `${actionPath}.speaker`, "non-actor speaker cannot contain actor speaker fields");
                            }
                            if (!NON_ACTOR_SPEAKER_KINDS.includes(action.speaker.kind)) {
                                err("speaker_kind", `${actionPath}.speaker.kind`, "invalid non-actor speaker kind");
                            }
                            if (action.speaker.label !== undefined && !isStr(action.speaker.label)) {
                                err("speaker_label", `${actionPath}.speaker.label`, "speaker label must be a string");
                            }
                        }
                        else {
                            err("speaker", `${actionPath}.speaker`, "speaker needs actor_id or kind");
                        }
                    }
                    else {
                        if ("parenthetical" in action) {
                            err("parenthetical_type", `${actionPath}.parenthetical`, "parenthetical only belongs to speaking actions");
                        }
                        if ("speaker" in action) {
                            err("speaker_type", `${actionPath}.speaker`, "speaker only belongs to speaking actions");
                        }
                    }
                    if (action.transition_prompt !== undefined) {
                        const prompt = action.transition_prompt;
                        if (!isObj(prompt)
                            || !isStr(prompt.target)
                            || !isStr(prompt.process)
                            || prompt.process === ""
                            || !isStr(prompt.contrast)
                            || prompt.contrast === "") {
                            err("transition_prompt", `${actionPath}.transition_prompt`, "transition_prompt requires non-empty target/process/contrast");
                        }
                        else if (!assetKinds.has(prompt.target)) {
                            err("transition_target", `${actionPath}.transition_prompt.target`, `asset ${prompt.target} not found`);
                        }
                    }
                });
                validateActionTimestampOrder(scene.actions, `${scenePath}.actions`, err);
            });
        });
    }
    return { ok: errors.length === 0, errors, warnings };
}
